import { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { isProtectedBaseRefusal, sendToPeter, type ExistingFiling, type FromBobResult, type PeterIssue } from "../api/peterHandoff";
import { canRefile, peterStateFor, safePrUrl, type PeterState } from "../lib/peterHandoff";

/** After a filing: the rows it created, and the findings that already had an issue, which the page re-reads. */
export type OnFiled = (created: PeterIssue[], existing: ExistingFiling[]) => void;

type Finding = { id: number; status?: unknown; regressed_count?: number | null };

/** Whether "Send to Peter" applies: an open finding Peter has no live issue for. */
export function canSendToPeter(finding: Finding, issues: readonly PeterIssue[]): boolean {
  const state = peterStateFor(finding, issues);
  return finding.status === "open" && (state.kind === "none" || state.kind === "came_back" || canRefile(state.issue));
}

/** A run that stopped can still have a pull request: startup recovery opens them for errored issues. */
function StoppedPr({ issue }: { issue: PeterIssue }) {
  if (typeof issue.pr_url !== "string" || !issue.pr_url) return null;
  const url = safePrUrl(issue.pr_url);
  return url ? (
    <>. Its <a href={url} target="_blank" rel="noopener noreferrer">pull request</a> is open</>
  ) : (
    <>. A pull request exists, but its address is not a GitHub one, so it is not linked</>
  );
}

/** Text only: every string here reaches React as a child. */
function StateLine({ state }: { state: PeterState }) {
  if (state.kind === "none") return null;
  const n = `#${state.issue.id}`;
  const branch = typeof state.issue.branch === "string" && state.issue.branch ? state.issue.branch : null;
  switch (state.kind) {
    case "open":
      return (
        <span className="cr-peter" data-peter="open" title="Filed from Bob's ledger. Nothing runs until a project member presses Start in the Issues panel.">
          Filed for Peter as issue {n}, waiting for someone to start it
        </span>
      );
    case "working":
      return <span className="cr-peter" data-peter="working">Peter is working on this (issue {n})</span>;
    case "stopped":
      return (
        <span className="cr-peter" data-peter="stopped">
          Peter stopped on issue {n}
          {state.issue.status_note ? `: ${state.issue.status_note}` : ""}
          <StoppedPr issue={state.issue} />
        </span>
      );
    case "pr":
      return (
        <span className="cr-peter" data-peter="pr">
          {state.url ? (
            <>
              Peter opened a fix for this{branch ? ` on ${branch}` : ""} (issue {n}):{" "}
              <a href={state.url} target="_blank" rel="noopener noreferrer">pull request</a>
            </>
          ) : (
            // Only a GitHub https url is ever linked; see safePrUrl.
            <>Peter opened a pull request for this (issue {n}). Its address is not a GitHub one, so it is not linked.</>
          )}
        </span>
      );
    case "no_pr":
      return (
        <span className="cr-peter" data-peter="no_pr">
          Peter finished issue {n} without a pull request: {state.issue.pr_reason}
        </span>
      );
    case "pushed":
      return (
        <span className="cr-peter" data-peter="pushed" title="Peter retries opening the pull request when the daemon restarts.">
          Peter finished issue {n} and pushed {branch ?? "his branch"}. Its pull request is not open yet.
        </span>
      );
    case "no_change":
      return (
        <span className="cr-peter" data-peter="no_change">
          Peter worked on issue {n} and found nothing to change
        </span>
      );
    case "push_failed":
      return (
        <span className="cr-peter" data-peter="push_failed">
          Peter could not push his work on issue {n}
          {state.issue.status_note ? `: ${state.issue.status_note}` : ""}
          <StoppedPr issue={state.issue} />
        </span>
      );
    case "crashed":
      return (
        <span className="cr-peter" data-peter="crashed">
          Peter&apos;s run on issue {n} crashed
          {state.issue.status_note ? `: ${state.issue.status_note}` : ""}
          <StoppedPr issue={state.issue} />
        </span>
      );
    case "came_back":
      return (
        <span className="cr-peter" data-peter="came_back" title="Peter finished an issue for an earlier sighting, and Bob has seen the defect again since.">
          Peter finished issue {n}, and this finding came back after it
        </span>
      );
  }
}

/** A finding's hand-off to Peter: what Peter did with it, and "Send to Peter"
 *  while nothing is in flight. */
export default function PeterHandoff(
  { project, finding, baseBranch, issues, onFiled, buttonSlot }: {
    project: string;
    finding: Finding;
    baseBranch: string | undefined;
    issues: readonly PeterIssue[];
    onFiled: OnFiled;
    /** Where the send button goes when it lives apart from the state, as in a phone's bottom bar. */
    buttonSlot?: HTMLElement | null;
  },
) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refusedFor, setRefusedFor] = useState<string | null>(null);
  const state = peterStateFor(finding, issues);
  const canSend = canSendToPeter(finding, issues);

  const send = useCallback(async () => {
    if (busy || !baseBranch) return;
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const r: FromBobResult = await sendToPeter(project, { baseBranch, findingIds: [finding.id] });
      const existing = r.existing.filter((e) => e.findingId === String(finding.id));
      onFiled(r.created, existing);
      if (r.created.length > 0) setNote(`Filed as issue #${r.created[0]!.id}. An admin starts it from the Issues panel.`);
      else if (existing.length > 0) setNote(`Already filed as issue #${existing[0]!.issueId}.`);
      else if (r.notFound.includes(String(finding.id))) setNote("Bob no longer has this finding, so nothing was filed.");
      else setNote("Nothing was filed.");
    } catch (e) {
      setError((e as Error).message);
      if (isProtectedBaseRefusal(e)) setRefusedFor(baseBranch);
    } finally {
      setBusy(false);
    }
  }, [busy, baseBranch, project, finding.id, onFiled]);
  const refused = refusedFor !== null && refusedFor === baseBranch;

  if (!canSend && state.kind === "none" && !note && !error) return null;

  const button = canSend && (
    <button
      type="button"
      className="bob-btn bob-btn-primary cr-peter-send"
      disabled={busy || !baseBranch || refused}
      title={refused && error
        ? error
        : baseBranch
        ? `Files this finding as a Peter issue against ${baseBranch}. Nothing starts until a project member presses Start.`
        : "Bob's answer named no branch, so there is nothing to file this against."}
      onClick={() => void send()}
    >
      {busy ? "Sending…" : state.kind === "none" ? "Send to Peter" : "Send to Peter again"}
    </button>
  );
  return (
    <span className="cr-peter-handoff">
      <StateLine state={state} />
      {buttonSlot === undefined ? button : buttonSlot && button ? createPortal(button, buttonSlot) : null}
      {note && <span className="cr-peter-note">{note}</span>}
      {error && <span className="cr-error-line" role="alert">{error}</span>}
    </span>
  );
}

/** Every open finding to Peter in one call, confirmed first. Findings already
 *  filed are skipped, so a second press files only what is new. */
export function SendAllToPeter(
  { project, baseBranch, openCount, onFiled }: {
    project: string;
    baseBranch: string | undefined;
    openCount: number;
    onFiled: OnFiled;
  },
) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refusedFor, setRefusedFor] = useState<string | null>(null);

  const send = useCallback(async () => {
    if (busy || !baseBranch) return;
    setConfirming(false);
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const r = await sendToPeter(project, { baseBranch });
      onFiled(r.created, r.existing);
      const parts = [`Filed ${r.created.length} new issue${r.created.length === 1 ? "" : "s"} for Peter`];
      if (r.existing.length > 0) parts.push(`${r.existing.length} already filed`);
      if (r.solved.length > 0) parts.push(`${r.solved.length} skipped as marked Solved`);
      setNote(`${parts.join(", ")}.`);
    } catch (e) {
      setError((e as Error).message);
      if (isProtectedBaseRefusal(e)) setRefusedFor(baseBranch);
    } finally {
      setBusy(false);
    }
  }, [busy, baseBranch, project, onFiled]);

  if (!baseBranch || openCount <= 0) return null;
  const refused = refusedFor === baseBranch;

  return (
    <span className="cr-peter-all">
      <button
        type="button"
        className="bob-btn"
        disabled={busy || refused}
        title={refused && error ? error : undefined}
        aria-expanded={confirming}
        onClick={() => setConfirming(!confirming)}
      >
        {busy ? "Sending…" : "Send all open findings to Peter"}
      </button>
      {confirming && (
        <span className="cr-confirm" role="dialog" aria-label="Send all open findings to Peter">
          <span>
            Files one Peter issue for every open finding on {baseBranch} that nobody marked Solved, skipping the ones
            already filed. Nothing starts until a project member presses Start.
          </span>
          <button type="button" className="bob-btn" onClick={() => void send()}>File them</button>
          <button type="button" className="bob-btn" onClick={() => setConfirming(false)}>Cancel</button>
        </span>
      )}
      {note && <span className="cr-peter-note">{note}</span>}
      {error && <span className="cr-error-line" role="alert">{error}</span>}
    </span>
  );
}
